import React, { useEffect, useRef, useState } from 'react';
import usePageTitle from "../../../hooks/usePageTitle";

interface VisibilityState {
    [key: string]: boolean;
}

interface AnimatedElementProps {
    children: React.ReactNode;
    className?: string;
    id: string;
    style?: React.CSSProperties;
}

interface CardProps {
    icon: string;
    title: string;
    items: string[];
    id: string;
}

interface FeatureBoxProps {
    icon: string;
    title: string;
    description: string;
}

interface StepProps {
    number: number;
    title: string;
    description: string;
    id: string;
}

const AnimatedElement: React.FC<AnimatedElementProps> = ({
                                                             children,
                                                             className = '',
                                                             id,
                                                             style = {}
                                                         }) => {
    const [isVisible, setIsVisible] = useState(false);
    const elementRef = useRef<HTMLDivElement | null>(null);

    usePageTitle("북밋 - 작가와 독자가 만나는 공간");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        // 컴포넌트 언마운트 시 옵저버 해제
        return () => {
            if (elementRef.current) {
                observer.unobserve(elementRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={elementRef}
            data-id={id}
            className={`${className} ${isVisible ? 'animate-visible' : 'animate-hidden'}`}
            style={style}
        >
            {children}
        </div>
    );
};

const BookmeetIntro: React.FC = () => {

    const Card: React.FC<CardProps> = ({ icon, title, items, id }) => (
        <AnimatedElement
            id={id}
            className="
            group
            bg-white p-8 rounded-2xl relative overflow-hidden transition-all duration-300
            shadow-[0_10px_30px_rgba(25,207,122,0.1)]
            border-t-4 border-t-[#19CF7A]
            hover:-translate-y-2
            hover:shadow-[0_20px_40px_rgba(29,185,84,0.15)]
        "
        >

            <span
                className="
                before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full
                before:bg-gradient-to-r
                before:from-transparent before:via-[rgba(29,185,84,0.1)] before:to-transparent
                before:transition-all before:duration-500
                group-hover:before:left-[100%]
            "
            />

            <span className="text-2xl mb-4 block relative z-10">{icon}</span>
            <h4 className="text-xl font-bold mb-4 relative z-10" style={{ color: '#19CF7A' }}>{title}</h4>
            <ul className="list-none p-0 relative z-10">
                {items.map((item, index) => (
                    <li key={index} className="py-1 pl-4 relative text-gray-600 text-sm leading-6">
                        <span className="absolute left-0 font-bold" style={{ color: '#19CF7A' }}>▸</span>
                        {item}
                    </li>
                ))}
            </ul>
        </AnimatedElement>
    );

    const FeatureBox: React.FC<FeatureBoxProps> = ({ icon, title, description }) => (
        <div
            className="
            bg-white p-6 rounded-xl text-center transition-all duration-300
            border-2 border-[rgba(25,207,122,0.1)]
            shadow-[0_5px_20px_rgba(25,207,122,0.1)]
            hover:-translate-y-1
            hover:border-[rgba(25,207,122,0.3)]
            hover:shadow-[0_10px_25px_rgba(25,207,122,0.15)]
        "
        >
            <h6 className="text-base m-0 font-semibold flex items-center justify-center" style={{ color: '#19CF7A' }}>
                <span className="text-lg mr-2">{icon}</span>
                {title}
            </h6>
            <p className="text-sm text-gray-600 mt-2 mb-0 leading-snug text-center">{description}</p>
        </div>
    );

    const Step: React.FC<StepProps> = ({ number, title, description, id }) => (
        <AnimatedElement id={id} className="text-center">
            <div
                className="w-12 h-12 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-4"
                style={{
                    background: 'linear-gradient(45deg, #19CF7A, #19CF7A)',
                    animation: `bounce 2s ease-in-out infinite ${number * 0.5}s`
                }}
            >
                {number}
            </div>
            <h5 className="text-lg mb-2" style={{ color: '#19CF7A' }}>{title}</h5>
            <p className="text-sm text-gray-600 text-center">{description}</p>
        </AnimatedElement>
    );

    return (
        <div className="text-gray-800 overflow-x-hidden w-full">

            <section className="flex items-center justify-center text-center relative bg-white" style={{ minHeight: '50vh' }}>
                <div className="max-w-6xl mx-auto px-5 relative z-10">
                    <div className="flex items-center justify-center gap-2 mb-12 text-5xl font-bold text-gray-800 logo-animation">
                        <span>북밋</span>
                        <span className="text-2xl">🖋</span>
                    </div>

                    <h1 className="text-[35px] font-medium text-gray-800 mb-[0.3rem] title-slide-left" style={{ letterSpacing: '-0.5px' }}>
                        아날로그의 온기를 온라인으로,
                    </h1>

                    <h2 className="text-lg text-gray-600 mb-4 max-w-3xl mx-auto font-normal subtitle-slide-right" style={{ letterSpacing: '0.2px', lineHeight: 1.5 }}>
                        작가의 문장이 독자의 마음에 가닿는 공간 '북밋'에 어서 오세요!
                    </h2>

                    <a
                        href="https://bookmeet.kr/"
                        className="inline-block text-white py-3 px-8 rounded-full font-bold transition-all duration-300 hover:-translate-y-1 hover:shadow-xl text-base mt-4"
                        style={{
                            background: 'linear-gradient(45deg, #19CF7A, #19CF7A)',
                            boxShadow: '0 5px 15px rgba(25, 207, 122, 0.4)'
                        }}
                    >
                        지금 시작하기
                    </a>
                </div>
            </section>

            {/* Background Section */}
            <section className="py-16" style={{ backgroundColor: '#f5f5f5' }}>
                <div className="max-w-6xl mx-auto px-5">
                    <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
                        🎯 플랫폼 탄생 배경
                    </h2>
                    <div className="max-w-4xl mx-auto text-left">
                        <p className="text-base leading-7 mb-6 text-justify">
                            독자는 어떤 기준으로 책을 선택할까요? 작가 소개를 먼저 읽는 사람, 장르와 주제를 살펴보는 사람, 독립출판 여부를 확인하는 사람까지 그 기준은 참으로 다양합니다.
                        </p>

                        <p className="text-base leading-7 mb-6 text-justify">
                            저희는 이 '다양성'에 주목했습니다. OTT 플랫폼이 영화와 드라마를 취향별로 큐레이션해주듯이, 책의 세계에도 이런 편리함을 가져오고 싶었습니다. 독립출판물부터 기성 작가의 작품까지, 모든 책이 동등하게 독자의 취향에 따라 발견될 수 있는 공간을 만들고자 했습니다.
                        </p>

                        <p className="text-base leading-7 mb-6 text-justify">
                            책을 읽다가 궁금했던 작가들의 출간 계기부터, 작가가 직접 쓴 자기 소개 이야기, 독자에게 보여주고 싶은 엽서들까지. 온라인에서는 좀처럼 접하기 어려운 작품과 작가의 진솔한 이야기를 이곳에서 편안하게 만나보세요. 북페어나 북토크에서 작가와 직접 마주하는 그 특별한 순간을 온라인에서도 생생하게 경험할 수 있습니다.
                            작품을 사랑하는 독자의 마음과 독자의 반응이 궁금한 작가의 마음이 어우러지는 곳, 북밋에서 새로운 문학적 경험을 시작해보세요! </p>
                    </div>
                </div>
            </section>

            {/* Writers Section */}
            <section className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-5">
                    <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
                        📖 작가님이라면!
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-8 mt-8">
                        <Card
                            id="writer-card1"
                            icon="🌟"
                            title="무료로 나와 작품 홍보를 해보세요!"
                            items={[
                                "출판사나 제3자의 개입 없이, 직접 독자에게 다가갈 수 있습니다",
                                "작품 출간 계기부터 손글씨 자기소개까지, 진솔한 이야기를 전달하세요.",
                                "비용 지불에 대한 부담감 없이 자유롭게 나만의 이야기를 써 내려가 보세요.",
                                "독자의 '좋아요'와 '팔로우'를 통해 작품과 자신에 대한 반응을 직접 확인할 수 있습니다"
                            ]}
                        />

                        <Card
                            id="writer-card2"
                            icon="📦"
                            title="어떤 것을 얻을 수 있나요?"
                            items={[
                                "작가 중심의 플랫폼을 통한 작가 개인 및 작품 홍보 효과",
                                "독자의 반응을 손쉽게 확인할 수 있음",
                                "더이상 남는 엽서는 그만! 작품과 함께 엽서를 등록해 독자들에게 더 풍부한 콘텐츠를 지속가능한 방식으로 전달해 보세요."
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* Readers Section */}
            <section className="py-16" style={{ backgroundColor: '#f5f5f5' }}>
                <div className="max-w-6xl mx-auto px-5">
                    <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
                        📚 내가 독자라면,
                    </h2>

                    <div className="grid grid-cols-2 md:grid-cols-1 gap-8 mt-8">
                        <Card
                            id="reader-card1"
                            icon="🎨"
                            title="내 취향대로 알아보는 콘텐츠"
                            items={[
                                "작가님이 쓰는 글 유형, 장르에 맞춰 원하는 책을 쉽게 찾을 수 있어요.",
                                "작가 중심의 콘텐츠로 작품 뒤의 이야기까지 깊이 있게 알아볼 수 있어요.",
                                "독립출판물부터 기성 작품까지 폭넓은 선택권을 제공해요."
                            ]}
                        />

                        <Card
                            id="reader-card2"
                            icon="🤝"
                            title="작가와의 특별한 교류"
                            items={[
                                "작가가 직접 전하는 작품 이야기와 창작 과정을 만나보세요.",
                                "엽서에 작성된 작가의 말을 통해 작가와 작품을 시각적으로 이해할 수 있습니다.",
                                "'좋아요'와 '팔로우'로 좋아하는 작가와 작품을 나만의 컬렉션으로 저장해 보세요."
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-5">
                    <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
                        🛠️ 작가 등록 프로세스 안내
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid grid-cols-3 md:grid-cols-1 gap-8 mt-8">
                            <Step
                                id="step1"
                                number={1}
                                title="자격 확인"
                                description="종이책 1권 이상 출간 경험자라면 누구나 등록 가능해요."
                            />
                            <Step
                                id="step2"
                                number={2}
                                title="간편 가입"
                                description="간편 로그인으로 빠른 회원가입 후 바로 작가로 등록해 보세요."
                            />
                            <Step
                                id="step3"
                                number={3}
                                title="신뢰성 검증"
                                description="출간작 정보 확인을 통한 신뢰성 있는 등록 프로세스가 진행됩니다."
                            />
                        </div>

                        <h2 className="text-2xl font-bold text-center mt-12 mb-8 text-gray-800">
                            주요 기능을 소개해 드릴게요
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-1 gap-6 mt-8 max-w-2xl mx-auto">
                            <FeatureBox
                                icon="❤️"
                                title="좋아요"
                                description="마음에 드는 작가, 작품, 엽서에 반응 표시해 보세요."
                            />
                            <FeatureBox
                                icon="➕"
                                title="팔로우"
                                description="관심 있는 작가를 팔로잉할 수 있습니다."
                            />
                            <FeatureBox
                                icon="📑"
                                title="나의 책갈피"
                                description="독자용 개인 보관함으로 저장한 콘텐츠 관리를 해보세요."
                            />
                            <FeatureBox
                                icon="📚"
                                title="나의 책장"
                                description="작가용 대시보드로 받은 반응과 통계 확인이 가능해요."
                            />
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 mt-12 max-w-3xl mx-auto relative shadow-lg">
                            <div
                                className="absolute top-0 left-8 right-8 h-px"
                                style={{ background: 'linear-gradient(to right, transparent, #19CF7A, transparent)' }}
                            ></div>

                            <h3 className="text-2xl mb-6 text-center relative flex items-center justify-center" style={{ color: '#19CF7A' }}>
                                💌 엽서북은 어떤 기능인가요?
                            </h3>

                            <ul className="list-none p-0">
                                <li className="py-3 pl-8 relative text-base leading-6 text-gray-600">
                                    <span className="absolute left-0 top-3 text-sm" style={{ color: '#19CF7A' }}>▸</span>
                                    작가가 직접 제작한 책 관련 엽서 컬렉션을 모아둔 탭입니다.
                                </li>
                                <li className="py-3 pl-8 relative text-base leading-6 text-gray-600">
                                    <span className="absolute left-0 top-3 text-sm" style={{ color: '#19CF7A' }}>▸</span>
                                    북페어나 동네 책방에서 만나볼 수 있는 작가들의 책 관련 엽서를 온라인으로 편리하게 감상할 수 있도록 구현했어요.
                                </li>
                                <li className="py-3 pl-8 relative text-base leading-6 text-gray-600">
                                    <span className="absolute left-0 top-3 text-sm" style={{ color: '#19CF7A' }}>▸</span>
                                    저작권 동의 후 작가가 직접 업로드하는 안전한 시스템으로, 로그인 이후 다운로드가 가능합니다.
                                </li>
                                <li className="py-3 pl-8 relative text-base leading-6 text-gray-600">
                                    <span className="absolute left-0 top-3 text-sm" style={{ color: '#19CF7A' }}>▸</span>
                                    작가가 쓴 엽서에 대한 글을 읽을 수 있으며, 하단 아이콘을 통해 작가의 SNS 계정도 함께 들어가볼 수 있어요.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 text-white text-center" style={{ backgroundColor: '#191414' }}>
                <div className="max-w-6xl mx-auto px-5">
                    <h2 className="text-3xl font-bold mb-4 text-white">독자는 작가에게, 작가는 독자에게로.</h2>
                    <p className="text-lg mb-8">서로의 마음을 이 특별한 공간에서 전해보세요!</p>
                    <a
                        href="https://bookmeet.kr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-white py-3 px-8 rounded-full font-bold transition-all duration-300 hover:-translate-y-1 hover:shadow-xl text-base"
                        style={{
                            background: 'linear-gradient(45deg, #19CF7A, #19CF7A)',
                            boxShadow: '0 5px 15px rgba(25, 207, 122, 0.4)'
                        }}
                    >
                        북밋 시작하기
                    </a>
                </div>
            </footer>
        </div>
    );
};

export default BookmeetIntro;
